import { useCallback, useEffect, useState } from 'react'
import { landingPageEventCategories } from '../../services/analytics-service/categories'
import { landingPageEventActions } from '../../services/analytics-service/actions'
import { landingPageEventLabels } from '../../services/analytics-service/labels'
import AnalyticsService from '../../services/analytics-service'
import ButtonTransport from '../../transports/common/button-transport'
import { useSelector } from 'react-redux'
import RootState from '../../redux/types'
import { useUserDetails } from '../../hooks/users'
import { Role } from '../search-result-listing/header-section/types'
import { useInView } from 'react-intersection-observer'
import { MobileSlidesConfig, trackListings } from './utils'
import { ListingSectionObject } from '../../transports/common/listing-section-transport'
import { isServer } from '../../utils/platform-utils'
import { getTrackingArray } from '../utils'

const _trackingIntersectionObserverOptions = {
    root: null,
    threshold: 0.99,
    triggerOnce: true,
}

export const useLoadMoreSlides = ({
    currentSlideIndex,
    visibleSlides,
    currentTotalSlideCount,
    loadMoreSlides,
    finalTotalSlideCount,
}: {
    currentSlideIndex: number
    visibleSlides: number
    currentTotalSlideCount: number
    loadMoreSlides: () => void
    finalTotalSlideCount: number
}) => {
    const _validRangeStartIndex = currentSlideIndex + visibleSlides
    useEffect(() => {
        if (currentTotalSlideCount > visibleSlides && currentTotalSlideCount < finalTotalSlideCount) {
            if (visibleSlides === 1) {
                if (currentSlideIndex === currentTotalSlideCount - 2) {
                    loadMoreSlides()
                }
            } else {
                if (
                    _validRangeStartIndex <= currentTotalSlideCount - 1 &&
                    currentTotalSlideCount - 1 <= _validRangeStartIndex + visibleSlides - 1
                ) {
                    loadMoreSlides()
                }
            }
        }
    }, [
        currentTotalSlideCount,
        visibleSlides,
        currentSlideIndex,
        _validRangeStartIndex,
        loadMoreSlides,
        finalTotalSlideCount,
    ])
}

export const useLandingPageHeader = ({ url, listRentalButton }: { url: string; listRentalButton: ButtonTransport }) => {
    const wishlistCount = useSelector((state: RootState) => state.wishlist.count)
    const { userDetails } = useUserDetails()
    const isLandlordRole = userDetails?.tfwPrimaryRole === Role.CUSTOMER
    const isGuestRole = userDetails?.tfwPrimaryRole === Role.TOURIST
    const inquiryCount = userDetails?.inquiryCount

    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const [isUserSidebarOpen, setIsUserSidebarOpen] = useState(false)
    const toggleSidebar = useCallback(() => setIsSidebarOpen(!isSidebarOpen), [isSidebarOpen, setIsSidebarOpen])
    const toggleUserSidebar = useCallback(
        () => setIsUserSidebarOpen(!isUserSidebarOpen),
        [isUserSidebarOpen, setIsUserSidebarOpen],
    )

    const [popUpBoxVisible, setPopUpBoxVisible] = useState(false)
    const togglePopUpBoxVisibility = useCallback(
        (isVisible: boolean) => setPopUpBoxVisible(isVisible),
        [setPopUpBoxVisible],
    )

    const listRentalClickHandler = useCallback(() => {
        if (listRentalButton && listRentalButton.url) {
            const eventCategory = landingPageEventCategories.headerSection.RENTAL
            const eventAction = landingPageEventActions.headerSection.RENTAL
            const eventLabel = landingPageEventLabels.headerSection.RENTAL
            AnalyticsService.trackEvent({ action: eventAction, category: eventCategory, label: eventLabel })

            window.location.href = listRentalButton.url
        }
    }, [listRentalButton?.url])

    const wishlistClickHandler = useCallback(() => {
        const eventCategory = `${landingPageEventCategories.headerSection.WISHLIST} - ${url}`
        const eventAction = landingPageEventActions.headerSection.WISHLIST
        const eventLabel = landingPageEventLabels.headerSection.WISHLIST
        AnalyticsService.trackEvent({ action: eventAction, category: eventCategory, label: eventLabel })
    }, [url])

    return {
        isSidebarOpen,
        toggleSidebar,
        isUserSidebarOpen,
        toggleUserSidebar,
        popUpBoxVisible,
        togglePopUpBoxVisibility,
        listRentalClickHandler,
        wishlistClickHandler,
        wishlistCount,
        isLandlordRole,
        isGuestRole,
        inquiryCount,
    }
}

export const usePropertyCardTracking = (
    propertyObject: ListingSectionObject,
    listingType?: string | null,
    index?: number,
) => {
    const [intersectionTargetRef, inView] = useInView(_trackingIntersectionObserverOptions)
    useEffect(() => {
        if (inView) {
            trackListings(getTrackingArray(true, propertyObject, listingType, index))
        }
    })

    const propertyCardClickTracking = useCallback(
        (propertyObject: ListingSectionObject, listingType?: string | null) => {
            trackListings(getTrackingArray(false, propertyObject, listingType))
        },
        [],
    )

    return { intersectionTargetRef, propertyCardClickTracking }
}

export const useResponsiveMobileSlides = ({ smallMobile, defaultMobile }: MobileSlidesConfig) => {
    const [visibleSlides, setVisibleSlides] = useState(() =>
        isServer ? defaultMobile : window.innerWidth < 400 ? smallMobile : defaultMobile,
    )

    useEffect(() => {
        if (isServer) {
            return
        }

        const handleResize = () => {
            setVisibleSlides(window.innerWidth < 400 ? smallMobile : defaultMobile)
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [smallMobile, defaultMobile])

    return visibleSlides
}
